import { Link } from "react-router-dom";
import {
    Users,
    MapPin,
    Upload,
    Shield,
    ArrowRight,
    Store,
    Globe,
    Inbox,
    BarChart3,
    Package,
    Mail,
    Layers,
} from "lucide-react";

const cards = [
    {
        title: "For Customers",
        description:
            "Stop emailing files back and forth. SignetPrint lets you send documents directly to any print shop's dashboard in seconds.",
        buttonText: "Start Sending Files",
        buttonLink: "/marketplace",
        icon: Users,
        iconBg: "bg-[#ff5323]",
        cardBg: "from-[#fff8f5] to-[#fff0ec]",
        border: "border-[#ffe4db]",
        buttonClass: "bg-[#ff5323] text-white hover:bg-[#e64a1f]",
        features: [
            {
                icon: MapPin,
                title: "Browse shops nearby",
                description:
                    "Find print shops with services, ratings, and hours at a glance.",
            },
            {
                icon: Upload,
                title: "Drag, drop, done",
                description:
                    "Upload any file - PDFs, images, design files - with optional print specs.",
            },
            {
                icon: Shield,
                title: "No lost files. Ever.",
                description:
                    "Files land directly in the shop's dashboard. No more attachments.",
            },
        ],
    },
    {
        title: "For Print Shops",
        description:
            "Turn SignetPrint into your digital shopfront. Receive files into your dashboard and let new customers discover you.",
        buttonText: "List Your Print Shop",
        buttonLink: "/onboarding/plan",
        icon: Store,
        iconBg: "bg-[#212326]",
        cardBg: "from-[#f8f8fa] to-[#f0f0f5]",
        border: "border-[#e4e5e7]",
        buttonClass: "bg-[#212326] text-white hover:bg-[#333]",
        features: [
            {
                icon: Globe,
                title: "Get discovered",
                description:
                    "Appear on the marketplace by location, services, and reviews.",
            },
            {
                icon: Inbox,
                title: "Centralised dashboard",
                description:
                    "Every order, file, and message in one clean interface.",
            },
            {
                icon: BarChart3,
                title: "Custom analytics",
                description:
                    "Track orders, peak times, popular services, and revenue trends.",
            },
        ],
    },
    {
        title: "For Specialist Print Shops",
        description:
            "If you print T-shirts, mugs, bags, signage - basically anything specialist. Get found on our marketplace and start receiving enquiries today.",
        buttonText: "List Your Specialist Shop",
        buttonLink: "/onboarding/plan",

        // we'll use custom icon instead of lucide
        customIcon: true,

        iconBg: "bg-[#7C3AED]",
        cardBg: "from-[#F5F0FF] to-[#F1EAFE]",
        border: "border-[#E7D8FF]",

        buttonClass:
            "bg-[#7C3AED] text-white hover:bg-[#6D28D9]",

        featureIconColor: "text-[#7C3AED]",

        features: [
            {
                icon: Globe,
                title: "Marketplace visibility",
                description:
                    "Get discovered for the specialist work you do best.",
            },
            {
                icon: Inbox,
                title: "Dashboard + email",
                description:
                    "Every enquiry in one place — and forwarded to your inbox.",
            },
            {
                icon: Mail,
                title: "No file uploads",
                description:
                    "Just conversations that lead to the right job, your way.",
            },
        ],
    },
    {
        title: "For Print Suppliers",
        description:
            "Sell paper, ink, toner, and equipment to print shops in your area. No cold calls, no trade shows, no middlemen.",
        buttonText: "List as a Supplier",
        buttonLink: "/onboarding/plan",
        icon: Package,
        iconBg: "bg-[#4a6cf7]",
        cardBg: "from-[#f0f4ff] to-[#f8f9ff]",
        border: "border-[#d6e0f5]",
        buttonClass: "bg-[#4a6cf7] text-white hover:bg-[#3b5de0]",
        featureIconColor: "text-[#4a6cf7]",
        features: [
            {
                icon: Globe,
                title: "Marketplace visibility",
                description:
                    "Your listing appears alongside print shops with its own filter.",
            },
            {
                icon: Mail,
                title: "Dashboard + email",
                description:
                    "Manage enquiries in your supplier dashboard - every new enquiry is also forwarded straight to your inbox.",
            },
            {
                icon: Package,
                title: "List your catalogue",
                description:
                    "Showcase paper, ink, toner, binding supplies, and equipment.",
            },
        ],
    },
];

const AboutPlatformSection = () => {
    return (
        <section className="bg-white border-b border-[#e4e5e7]">
            <div className="max-w-[1200px] mx-auto px-[16px] md:px-[40px] py-[56px] md:py-[80px]">

                {/* Header */}
                <div className="text-center mb-[48px] md:mb-[56px]">
                    <p className="font-semibold text-[13px] text-[#ff5323] tracking-[0.5px] uppercase mb-[8px]">
                        The platform
                    </p>

                    <h2 className="font-semibold text-[28px] md:text-[36px] text-[#212326] tracking-[-0.5px] mb-[12px]">
                        One platform. Four powerful experiences.
                    </h2>

                    <p className="font-medium text-[16px] text-[#717182] leading-[1.6] max-w-[600px] mx-auto">
                        SignetPrint is a marketplace that makes printing simple
                        for customers and profitable for shops. Here's how each
                        side works.
                    </p>
                </div>

                {/* Cards */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-[24px]">
                    {cards.map((card) => {
                        const Icon = card.icon;

                        return (
                            <div
                                key={card.title}
                                className={`bg-gradient-to-br ${card.cardBg} rounded-[20px] p-[28px] md:p-[32px] border ${card.border} flex flex-col`}
                            >

                                <div
                                    className={`w-[52px] h-[52px] rounded-[14px] ${card.iconBg} flex items-center justify-center mb-[20px]`}
                                >
                                    {card.customIcon ? (
                                        <svg
                                            width="26"
                                            height="26"
                                            viewBox="0 0 26 26"
                                            fill="none"
                                            xmlns="http://www.w3.org/2000/svg"
                                        >
                                            <path d="M14.5832 7.00004C14.9054 7.00004 15.1665 6.73887 15.1665 6.41671C15.1665 6.09454 14.9054 5.83337 14.5832 5.83337C14.261 5.83337 13.9999 6.09454 13.9999 6.41671C13.9999 6.73887 14.261 7.00004 14.5832 7.00004Z" fill="white" stroke="white" strokeWidth="2.33333" strokeLinecap="round" strokeLinejoin="round" />
                                            <path d="M19.25 11.6667C19.5721 11.6667 19.8333 11.4056 19.8333 11.0834C19.8333 10.7612 19.5721 10.5001 19.25 10.5001C18.9278 10.5001 18.6666 10.7612 18.6666 11.0834C18.6666 11.4056 18.9278 11.6667 19.25 11.6667Z" fill="white" stroke="white" strokeWidth="2.33333" strokeLinecap="round" strokeLinejoin="round" />
                                            <path d="M8.74996 8.16673C9.07213 8.16673 9.33329 7.90556 9.33329 7.58339C9.33329 7.26123 9.07213 7.00006 8.74996 7.00006C8.42779 7.00006 8.16663 7.26123 8.16663 7.58339C8.16663 7.90556 8.42779 8.16673 8.74996 8.16673Z" fill="white" stroke="white" strokeWidth="2.33333" strokeLinecap="round" strokeLinejoin="round" />
                                            <path d="M6.41659 14C6.73875 14 6.99992 13.7389 6.99992 13.4167C6.99992 13.0945 6.73875 12.8334 6.41659 12.8334C6.09442 12.8334 5.83325 13.0945 5.83325 13.4167C5.83325 13.7389 6.09442 14 6.41659 14Z" fill="white" stroke="white" strokeWidth="2.33333" strokeLinecap="round" strokeLinejoin="round" />
                                            <path d="M12.8333 1.16669C6.41663 1.16669 1.16663 6.41669 1.16663 12.8334C1.16663 19.25 6.41663 24.5 12.8333 24.5C13.9136 24.5 14.756 23.6297 14.756 22.5307C14.756 22.0209 14.546 21.5565 14.2461 21.2182C13.9078 20.881 13.7351 20.4575 13.7351 19.9057C13.7307 19.6489 13.778 19.3939 13.8742 19.1558C13.9705 18.9177 14.1137 18.7014 14.2952 18.5198C14.4768 18.3382 14.6931 18.195 14.9312 18.0988C15.1693 18.0026 15.4244 17.9553 15.6811 17.9597H18.0098C21.5693 17.9597 24.4906 15.0395 24.4906 11.48C24.4591 5.84735 19.2045 1.16669 12.8333 1.16669Z" stroke="white" strokeWidth="2.33333" strokeLinecap="round" strokeLinejoin="round" />
                                        </svg>
                                    ) : (
                                        <Icon className="w-[24px] h-[24px] text-white" />
                                    )}
                                </div>

                                <h3 className="font-semibold text-[20px] text-[#212326] mb-[8px]">
                                    {card.title}
                                </h3>

                                <p className="font-medium text-[14px] text-[#717182] leading-[1.6] mb-[24px]">
                                    {card.description}
                                </p>

                                <div className="flex flex-col gap-[14px] mb-[24px] flex-1">
                                    {card.features.map((feature) => {
                                        const FeatureIcon = feature.icon;

                                        return (
                                            <div
                                                key={feature.title}
                                                className="flex items-start gap-[12px]"
                                            >
                                                <div className="w-[36px] h-[36px] rounded-[10px] bg-white flex items-center justify-center shrink-0 mt-[1px]">
                                                    <FeatureIcon
                                                        className={`w-[18px] h-[18px] ${card.featureIconColor ||
                                                            "text-[#212326]"
                                                            }`}
                                                    />
                                                </div>

                                                <div>
                                                    <p className="font-semibold text-[14px] text-[#212326] mb-[2px]">
                                                        {feature.title}
                                                    </p>

                                                    <p className="font-medium text-[13px] text-[#717182] leading-[1.5]">
                                                        {feature.description}
                                                    </p>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>

                                <Link
                                    to={card.buttonLink}
                                    className={`inline-flex items-center justify-center gap-[8px] px-[20px] py-[12px] rounded-[10px] font-semibold text-[14px] transition-colors no-underline ${card.buttonClass}`}
                                >
                                    {card.buttonText}
                                    <ArrowRight className="w-[14px] h-[14px]" />
                                </Link>
                            </div>
                        );
                    })}
                </div>

            </div>
        </section>
    );
};

export default AboutPlatformSection;
